import { useCallback, useMemo, useState } from 'react';
import useMuiTableBuilderV2 from 'hooks/useMuiTableBuilderV2';
import getTableConfig from './tableConfig';
import { useTeamsV2ListItemBuilder } from './useTeamsV2ListItemBuilder';
import { SearchTeamsByFieldsPayload, TeamSearchItem } from 'TeamModule/api/searchTeamsByFieldsApi';
import { Box, TablePaginationProps } from '@mui/material';
import { TeamsV2CustomRowProps } from './types';
import { ORDER_DIRECTION } from '@constants';
import { useDeactivateTeamsModal } from 'TeamModule/components/DeactivateTeamsModal/useDeactivateTeamsModal';
import { useTeamsBulkActionMenu } from './useTeamsBulkActionMenu';
import { Button } from '@mui/material';
import TeamsPaginationFooter from 'TeamModule/components/TeamsV2Page/components/TeamsPaginationFooter';
import { useScheduleDeactivateTeamsModal } from 'TeamModule/components/ScheduleDeactivateTeamsModal/useScheduleDeactivateTeamsModal';

const tableConfig = getTableConfig();

export const TeamsV2Table = ({
  teams,
  isLoading,
  selectedIds,
  hasSelectionOnPage,
  allOnPageSelected,
  isIndeterminateOnPage,
  onToggleRow,
  onToggleSelectAll,
  paginationProps,
  orderBy,
  orderDirection,
  onSort,
  onRowClick,
  onAfterDeactivate,
  filtersPayload,
  exportFileName,
}: {
  teams: TeamSearchItem[];
  isLoading: boolean;
  selectedIds: Set<string>;
  hasSelectionOnPage: boolean;
  allOnPageSelected: boolean;
  isIndeterminateOnPage: boolean;
  onToggleRow: (teamId: string) => void;
  onToggleSelectAll: (checked: boolean) => void;
  paginationProps: TablePaginationProps & { indexBase?: 0 | 1 };
  orderBy: string;
  orderDirection: (typeof ORDER_DIRECTION)[keyof typeof ORDER_DIRECTION];
  onSort: TeamsV2CustomRowProps['onSort'];
  onRowClick?: (team: TeamSearchItem) => void;
  onAfterDeactivate?: VoidFunction;
  filtersPayload: SearchTeamsByFieldsPayload;
  exportFileName?: string;
}) => {
  const listItemsBuilder = useTeamsV2ListItemBuilder();
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const onToggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const mainList = useMemo(() => {
    const listItems = listItemsBuilder({ teams: teams.map((t) => ({ ...t })), expandedIds });
    // Attach row click to non-deleted teams
    const augmented = listItems.map((li) => {
      if (li.rowType === 'DATA' && li.team) {
        const team = li.team;
        const isDeleted = Boolean(team.dataDeletionExecutedOnDate);
        return {
          ...li,
          onRowClick: !isDeleted && onRowClick ? () => onRowClick(team) : undefined,
        };
      }
      return li;
    });
    return { listItems: augmented };
  }, [listItemsBuilder, onRowClick, teams, expandedIds]);

  const _customRowProps = useMemo(
    () => ({
      selectedIds,
      onToggleRow,
      allOnPageSelected,
      isIndeterminateOnPage,
      onToggleSelectAll,
      orderBy,
      orderDirection,
      onSort,
      onRowClick,
      expandedIds,
      onToggleExpand,
    }),
    [
      allOnPageSelected,
      isIndeterminateOnPage,
      onRowClick,
      onSort,
      onToggleRow,
      onToggleSelectAll,
      orderBy,
      orderDirection,
      selectedIds,
      expandedIds,
      onToggleExpand,
    ],
  );

  const { openDeactivateTeamsModal } = useDeactivateTeamsModal();
  const { openScheduleDeactivateTeamsModal } = useScheduleDeactivateTeamsModal();

  const buildTargetsFromSelection = useCallback(() => {
    const selectedUuids = Array.from(selectedIds);
    const namesById: Record<number, string> = {};
    const teamTargets: Array<{ teamId: number; tenant: string; realmId: string }> = [];

    selectedUuids.forEach((uuid) => {
      const t = teams.find((row) => row.id === uuid);
      if (!t) return;
      const idNum = parseInt(t.teamId, 10);
      if (!Number.isNaN(idNum)) {
        namesById[idNum] = t.name;
        teamTargets.push({ teamId: idNum, tenant: t.tenant, realmId: t.realmId });
      }
    });

    return { namesById, teamTargets };
  }, [selectedIds, teams]);

  const onBulkDeactivate = useCallback(() => {
    const { namesById, teamTargets } = buildTargetsFromSelection();
    if (teamTargets.length === 0) return;
    openDeactivateTeamsModal({ teamTargets, teamNamesById: namesById, onAfterDeactivate });
  }, [buildTargetsFromSelection, onAfterDeactivate, openDeactivateTeamsModal]);

  const { openTeamsBulkActionMenu, refreshTeamIntegrations, isRefreshingTeamIntegrations } =
    useTeamsBulkActionMenu();

  const onBulkScheduleDeactivate = useCallback(() => {
    const { namesById, teamTargets } = buildTargetsFromSelection();
    if (teamTargets.length === 0) return;
    openScheduleDeactivateTeamsModal({
      teamTargets,
      teamNamesById: namesById,
      onAfterSchedule: onAfterDeactivate,
    });
  }, [buildTargetsFromSelection, onAfterDeactivate, openScheduleDeactivateTeamsModal]);

  const onRefreshIntegrations = useCallback(() => {
    const selectedUuids = Array.from(selectedIds);
    const teamIds: number[] = [];
    selectedUuids.forEach((uuid) => {
      const t = teams.find((row) => row.id === uuid);
      if (!t) return;
      const idNum = parseInt(t.teamId, 10);
      if (!Number.isNaN(idNum)) teamIds.push(idNum);
    });
    if (teamIds.length === 0) return;
    refreshTeamIntegrations({ teamIds });
  }, [selectedIds, teams, refreshTeamIntegrations]);

  const renderHeaderTools = useCallback(() => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          ml: 'auto',
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          onClick={(e) =>
            openTeamsBulkActionMenu({
              target: e.currentTarget,
              onDeactivate: onBulkDeactivate,
              onScheduleDeactivate: onBulkScheduleDeactivate,
              onRefreshIntegrations,
              filtersPayload,
              shouldDisableTeamDeactivation: !hasSelectionOnPage,
              shouldDisableTeamScheduleDeactivation: (() => {
                if (!hasSelectionOnPage) return true;
                const selectedUuids = Array.from(selectedIds);
                return selectedUuids.some((uuid) => {
                  const t = teams.find((row) => row.id === uuid);
                  if (!t) return true;
                  return !t.archivedAt && Boolean(t.scheduledArchiveDate);
                });
              })(),
              shouldDisableRefreshIntegrations: !hasSelectionOnPage || isRefreshingTeamIntegrations,
              exportFileName,
            })
          }
        >
          Actions
        </Button>
      </Box>
    );
  }, [
    hasSelectionOnPage,
    openTeamsBulkActionMenu,
    onBulkDeactivate,
    onBulkScheduleDeactivate,
    onRefreshIntegrations,
    isRefreshingTeamIntegrations,
    filtersPayload,
    selectedIds,
    teams,
    exportFileName,
  ]);

  const renderFooterOptions = useCallback(() => {
    return <TeamsPaginationFooter paginationProps={paginationProps} />;
  }, [paginationProps]);

  const { TableComponent } = useMuiTableBuilderV2({
    ...tableConfig,
    mainList,
    _customRowProps,
    isLoading,
    renderHeaderTools,
    renderFooterOptions,
    tableMaxHeight: '800px',
  });

  return <Box>{TableComponent}</Box>;
};

export default TeamsV2Table;
